import {
  TriangleAlert,
  AlertOctagon,
  Skull,
  Wind,
  Waves,
  Droplet,
  Droplets,
  Footprints,
  Flame,
  CloudFog,
  Hand,
  Shirt,
  Glasses,
  HardHat,
  UsersRound,
  ShieldAlert,
} from "lucide-react";

/**
 * Central catalogue of every GHS-style pictogram used across the site.
 * The Active-Substance display and the interactive Label Reader both read from
 * this list so the visual language is identical on both sides.
 */
export const PICTOGRAM_CATALOGUE = [
  {
    name: "Corrosive",
    icon: TriangleAlert,
    tone: "#c1442d",
    meaning:
      "Severe skin burns and permanent eye damage on contact. All four of our substances carry this — the difference is how fast, and how deep.",
    when: "The most common label you'll see on our tankers — every acid and caustic caries a corrosive class.",
  },
  {
    name: "Acute Toxicity",
    icon: Skull,
    tone: "#c1442d",
    meaning:
      "Skull-and-crossbones. Harmful if inhaled, ingested or absorbed. Especially the acid mists — do not stand downwind of a leak.",
    when: "Concentrated HCl and H2SO4 mist inhalation. Not on soda ash or dilute caustic solutions.",
  },
  {
    name: "Gas under pressure",
    icon: Wind,
    tone: "#4a6fa5",
    meaning:
      "The contents are under pressure and can fume aggressively — the tanker will off-gas heavily on opening in moist air.",
    when: "Concentrated HCl vapours in moist air. Also relevant during tanker venting.",
  },
  {
    name: "Reacts with water",
    icon: Waves,
    tone: "#c1442d",
    meaning:
      "Adding water to the substance causes a violent exothermic reaction — spatter and heat. Always add the substance to water when diluting, never the reverse.",
    when: "Concentrated H2SO4 is the classic case. Also solid NaOH added to water — always slow, always into water.",
  },
  {
    name: "Hygroscopic",
    icon: Droplet,
    tone: "#4A5C6E",
    meaning:
      "Draws moisture aggressively — from air, from spilled material, and from human tissue. Explains the deceptively deep burns on skin.",
    when: "H2SO4 is intensely hygroscopic — this is why an H2SO4 burn looks small and gets worse over hours.",
  },
  {
    name: "Slip hazard",
    icon: Footprints,
    tone: "#e8a33d",
    meaning:
      "Solutions are slippery on skin and on floors — much worse than the visual clue suggests. Contain and rinse promptly.",
    when: "Caustic soda solutions in particular — the classic 'soapy feel' on skin means active contact and needs a longer flush.",
  },
  {
    name: "Exothermic on dilution",
    icon: Flame,
    tone: "#c1442d",
    meaning:
      "Dilution generates heat that can boil water and spatter concentrated material. Always dose slowly into a large volume of water.",
    when: "H2SO4 concentrated dilution, and dissolving solid NaOH — both release enough heat to steam.",
  },
  {
    name: "Irritant",
    icon: AlertOctagon,
    tone: "#e8a33d",
    meaning:
      "Not corrosive at working strength, but still irritates eyes, skin and airways — especially the dust.",
    when: "Primary label on soda ash (Na2CO3) and dilute solutions of the other three.",
  },
  {
    name: "Dust hazard",
    icon: CloudFog,
    tone: "#4a6fa5",
    meaning:
      "Fine dust travels further than expected — use local exhaust ventilation and a dust mask when opening bulk containers or transfer points.",
    when: "Soda ash light-grade during transfer/bagging operations.",
  },
];

export const PICTO_INDEX = Object.fromEntries(
  PICTOGRAM_CATALOGUE.map((p) => [p.name, p])
);

export function getPictoIcon(name) {
  return PICTO_INDEX[name]?.icon || TriangleAlert;
}

/**
 * Given a free-text PPE item like "Full face shield" or "Neoprene gauntlet gloves",
 * pick the most appropriate lucide-react icon.
 */
export function getPpeIcon(item) {
  const s = (item || "").toLowerCase();
  if (s.includes("glove") || s.includes("gauntlet")) return Hand;
  if (s.includes("boot")) return Footprints;
  if (s.includes("face shield") || s.includes("glasses") || s.includes("goggle") || s.includes("eyewear")) return Glasses;
  if (s.includes("respirator") || s.includes("mask") || s.includes("cartridge")) return Wind;
  if (s.includes("suit") || s.includes("apron") || s.includes("coverall")) return Shirt;
  if (s.includes("buddy") || s.includes("standby") || s.includes("supervisor")) return UsersRound;
  if (s.includes("shower") || s.includes("eyewash") || s.includes("hydration") || s.includes("water")) return Droplets;
  if (s.includes("emergency")) return ShieldAlert;
  return HardHat;
}
